import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import p5 from "p5";
import knightImgSrc from "./knight.png"; 

// ... (Keep the validMoves array, Board, Knight, and Tile classes exactly as they are) ...

// Wrap the component in forwardRef
const KnightsTour = forwardRef((props, ref) => {
  const canvasRef = useRef(null);
  
  // Use a mutable ref to safely store the board instance across renders
  const boardRef = useRef(null);
  // Store a mutable reference to the completion status
  const completedRef = useRef(false);

  // Expose the reset function to parent components
  useImperativeHandle(ref, () => ({
    triggerReset() {
      if (boardRef.current) {
        boardRef.current.resetBoard();
        completedRef.current = false;
      }
    }
  }));

  useEffect(() => {
    let boardLength = 400;
    let tileLength = boardLength / 8;
    let continueTour = true;
    let state = { myP5: null, isUnmounted: false };

    const imgElement = new Image();
    imgElement.src = knightImgSrc; 

    imgElement.onload = () => {
      if (state.isUnmounted) return;

      const sketch = (p) => {
        let knightImg;

        p.setup = () => {
          p.createCanvas(boardLength, boardLength).parent(canvasRef.current);
          p.frameRate(6);
          
          knightImg = p.createImage(imgElement.width, imgElement.height);
          knightImg.drawingContext.drawImage(imgElement, 0, 0);

          // Assign the created board to our mutable ref
          boardRef.current = new Board(tileLength, knightImg, p);
        };

        p.draw = () => {
          if (continueTour && !completedRef.current) {
            completedRef.current = boardRef.current.moveKnight();
          }
          
          if (boardRef.current) {
            boardRef.current.renderBoard();
          }

          if (completedRef.current) {
            // Draw lines
            for (let i = 1; i <= boardRef.current.tilesVisited; i++) {
              let currentTile; let nextTile;
              for (let row of boardRef.current.tiles) {
                for (let tile of row) {
                  if (tile.tourPosition === i) currentTile = tile;
                  if (tile.tourPosition === i + 1) nextTile = tile;
                }
              }
              if (currentTile && nextTile) {
                p.stroke(0, 255, 0, 150); p.strokeWeight(2);
                p.line(
                  currentTile.x + currentTile.width / 2, currentTile.y + currentTile.height / 2,
                  nextTile.x + nextTile.width / 2, nextTile.y + nextTile.height / 2
                );
              }
            }
            p.textSize(32); p.stroke(0); p.strokeWeight(4); p.fill(255); p.textAlign(p.CENTER, p.CENTER);
            p.text('Knight has completed the tour :)', 20, p.height / 2, boardLength - 40);
          }
        };

        p.keyPressed = () => {
          if (p.keyCode === 32) {
            if (boardRef.current) {
              boardRef.current.resetBoard();
              completedRef.current = false;
            }
          }
        };
      };

      if (canvasRef.current) {
        canvasRef.current.innerHTML = "";
        const originalCheck = p5.prototype._checkForUserDefinedFunctions;
        p5.prototype._checkForUserDefinedFunctions = function() {};
        state.myP5 = new p5(sketch);
        p5.prototype._checkForUserDefinedFunctions = originalCheck;
      }
    };

    return () => {
      state.isUnmounted = true;
      if (state.myP5) {
        state.myP5.remove();
      }
    };
  }, []);

  return <div ref={canvasRef} />;
});

// Explicit component display name for React DevTools clean tracking
KnightsTour.displayName = "KnightsTour";

export default KnightsTour;


const validMoves = [
  [2, 1],
  [2, -1],
  [-2, 1],
  [-2, -1],
  [1, 2],
  [1, -2],
  [-1, 2],
  [-1, -2]
];

class Board {
  constructor(tileLength, knightIcon, p) {
    this.p = p;
    this.knightCanMove = true;
    this.tilesVisited = 1;
    this.tiles = this.generateTiles(tileLength);
    this.knight = this.getKnight(tileLength, knightIcon);
  }

  generateTiles(tileLength) {
    const tiles = [];
    for (let i = 0; i < 8; i++) {
      tiles[i] = [];
      for (let j = 0; j < 8; j++) {
        tiles[i][j] = new Tile(i * tileLength, j * tileLength, tileLength, tileLength, (i + j) % 2 === 0 ? 255 : 0, this.p);
      }
    }
    return tiles;
  }

  getKnight(tileLength, knightIcon) {
    let randomRow = Math.floor(Math.random() * 8);
    let randomCol = Math.floor(Math.random() * 8);
    this.tiles[randomRow][randomCol].visited = true;
    this.tiles[randomRow][randomCol].startedOn = true;
    this.tiles[randomRow][randomCol].tourPosition = this.tilesVisited;
    return new Knight(this.tiles[randomRow][randomCol].x, this.tiles[randomRow][randomCol].y, tileLength, tileLength, knightIcon, this.p);
  }

  moveKnight() {
    if (!this.knightCanMove) return false;

    let bestMove = this.getBestMove();

    if (bestMove) {
      const newX = this.knight.x + bestMove[0] * this.knight.width;
      const newY = this.knight.y + bestMove[1] * this.knight.height;
      this.knight.move(newX, newY);
      const newTileX = Math.round(newX / this.knight.width);
      const newTileY = Math.round(newY / this.knight.height);
      this.tilesVisited++;
      this.tiles[newTileX][newTileY].visited = true;
      this.tiles[newTileX][newTileY].tourPosition = this.tilesVisited;
      return false;
    } else {
      this.knightCanMove = false;
      return this.tilesVisited >= 64;
    }
  }

  getBestMove() {
    let bestMove = null;
    let leastValidMoves = Infinity;
    for (let i = 0; i < validMoves.length; i++) {
      const newX = this.knight.x + validMoves[i][0] * this.knight.width;
      const newY = this.knight.y + validMoves[i][1] * this.knight.height;

      if (newX >= 0 && newX < 8 * this.knight.width && newY >= 0 && newY < 8 * this.knight.height) {
        const tileX = Math.round(newX / this.knight.width);
        const tileY = Math.round(newY / this.knight.height);
        if (!this.tiles[tileX][tileY].visited) {
          let validMovesFromNewTile = 0;
          for (let j = 0; j < validMoves.length; j++) {
            const testX = tileX + validMoves[j][0];
            const testY = tileY + validMoves[j][1];
            if (testX >= 0 && testX < 8 && testY >= 0 && testY < 8 && !this.tiles[testX][testY].visited) {
              validMovesFromNewTile++;
            }
          }
          if (validMovesFromNewTile < leastValidMoves) {
            leastValidMoves = validMovesFromNewTile;
            bestMove = validMoves[i];
          }
        }
      }
    }
    return bestMove;
  }

  resetBoard() {
    this.knightCanMove = true;
    this.tilesVisited = 1;
    this.tiles = this.generateTiles(this.knight.width);
    this.knight = this.getKnight(this.knight.width, this.knight.icon);
  }

  renderBoard() {
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        this.tiles[i][j].renderTile();
      }
    }
    this.knight.renderKnight();

    if (!this.knightCanMove && this.tilesVisited < 64) {
      this.p.textSize(22);
      this.p.stroke(0);
      this.p.strokeWeight(4);
      this.p.fill(255, 0, 0);
      this.p.textAlign(this.p.CENTER, this.p.CENTER);
      this.p.text('Tour failed! Visited ' + this.tilesVisited + ' tiles.', 20, this.p.height / 2, this.p.width - 40);
    }
  }
}

class Knight {
  constructor(x, y, height, width, icon, p) {
    this.p = p; this.x = x; this.y = y; this.height = height; this.width = width; this.icon = icon;
  }
  renderKnight() {
    if (this.icon) {
      this.p.image(this.icon, this.x, this.y, this.width, this.height);
    }
  }
  move(x, y) { this.x = x; this.y = y; }
}

class Tile {
  constructor(x, y, height, width, colour, p) {
    this.p = p; this.x = x; this.y = y; this.height = height; this.width = width; this.colour = colour;
    this.visited = false; this.startedOn = false; this.tourPosition = 0;
  }
  renderTile() {
    this.p.stroke(0); this.p.strokeWeight(1); this.p.fill(this.colour); this.p.rect(this.x, this.y, this.width, this.height);
    if (this.visited) {
      this.p.fill(this.startedOn ? [0, 255, 0] : [255, 0, 0]);
      this.p.circle(this.x + this.width / 2, this.y + this.height / 2, this.width / 2);
      this.p.fill(235); this.p.textSize(16); this.p.textAlign(this.p.CENTER, this.p.CENTER);
      this.p.text(this.tourPosition, this.x + this.width / 2, this.y + this.height / 2);
    }
  }
}